import { Button, Dropdown, Flex, Typography } from 'antd'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FilterSearchIcon } from 'src/assets/icons'
import { useTableContext } from './TableProvider'
import CustomRangePicker from '../../range-picker'
import dayjs from 'dayjs'
import { IDateFilterProps } from '../table-setting.model'

const TableDateFilter: React.FC = () => {
  const {
    displayDateFilter,
    disableTableControl,
    defs,
    dateFilter,
    loading,
    handleSetDateFilter,
    resetDateFilter,
    onSearch,
    setDateFilter,
  } = useTableContext()
  const { t } = useTranslation()
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false)
  const [prevDate, setPrevDate] = useState<IDateFilterProps[]>([])

  const dateColumns = useMemo(() => defs.filter((item) => !item.hide && item.valueType === 'date') ?? [], [defs])

  const onToggleDropdown = useCallback(() => {
    const newState = !dropdownOpen
    if (newState) {
      setDateFilter?.(JSON.parse(JSON.stringify(prevDate ?? [])))
    } else {
      resetDateFilter?.()
    }
    setDropdownOpen(newState)
  }, [dropdownOpen, prevDate, resetDateFilter, setDateFilter])

  const closeDropdown = useCallback(() => setDropdownOpen(false), [])

  const renderColumnDropdown = useCallback(
    () => (
      <div className='date-filter-container'>
        <Flex className='date-filter-header'>
          <Typography>{t('common:filter')}</Typography>
          <Button type='text' onClick={closeDropdown}>
            x
          </Button>
        </Flex>
        <Flex
          style={{
            flexDirection: 'column',
            gap: 16,
          }}
          className='date-filter-section'
        >
          {dateColumns.map((item) => {
            const value = dateFilter?.find((_item) => _item.dateType === item.colId)
            const dateRangeValue = value
              ? [value.dateFrom ? dayjs(value.dateFrom) : null, value.dateTo ? dayjs(value.dateTo) : null]
              : []
            return (
              <CustomRangePicker
                key={item.colId}
                label={item.headerName}
                name={item.colId}
                allowEmpty={[true, true]}
                value={dateRangeValue}
                onChange={(dates: any) => {
                  handleSetDateFilter?.(item.colId, dates)
                }}
              />
            )
          })}
        </Flex>
        <Flex
          justify='flex-end'
          gap={8}
          style={{
            marginTop: 16,
            padding: 16,
          }}
        >
          <Button onClick={resetDateFilter} disabled={loading || dateFilter?.length === 0} style={{ fontWeight: 600 }}>
            {t('common:clear_all')}
          </Button>
          <Button
            type='primary'
            onClick={() => {
              onSearch?.({})
              closeDropdown()
              setPrevDate(JSON.parse(JSON.stringify(dateFilter ?? [])))
            }}
            loading={loading}
            disabled={loading}
          >
            {t('common:apply')}
          </Button>
        </Flex>
      </div>
    ),
    [closeDropdown, dateColumns, dateFilter, handleSetDateFilter, loading, onSearch, resetDateFilter, t],
  )

  if (!displayDateFilter || disableTableControl) {
    return <React.Fragment />
  }

  return (
    <Dropdown
      trigger={['click']}
      placement='bottomRight'
      open={dropdownOpen}
      dropdownRender={renderColumnDropdown}
      onOpenChange={onToggleDropdown}
      disabled={dateColumns.length === 0}
    >
      <Button id='column-button' icon={<FilterSearchIcon />}>
        {t('AllTransactions:viewTransaction.filter')}
      </Button>
    </Dropdown>
  )
}

export default TableDateFilter
